// Item icon cache (UI-2). Renders a 16×16 pixel-art icon per item once and caches
// its data URL (for CSS slot backgrounds) and a THREE texture (for dropped-item
// billboards). Block items sample their side tile from the world atlas; loose
// items (sticks, coal, tools) get small procedural sprites — no image assets
// (ARCH §D3). Pure client (ENG-1) — icons are presentation only.
import * as THREE from 'three';
import type { Registries } from '../../../sim/registry/load';
import type { RenderTable } from '../../../sim/mesh/atlasmap';
import { TILE_PX } from '../../../sim/mesh/atlasmap';
import { ICON_SIZE, iconToRgba, type IconData } from '../../../sim/registry/icon';

const TIER_COLOR: Record<string, [number, number, number]> = {
  wood: [150, 112, 62], gold: [244, 214, 84], stone: [128, 128, 132],
  iron: [216, 216, 222], diamond: [104, 220, 216], netherite: [78, 70, 76],
};

export class ItemIcons {
  private readonly canvases = new Map<number, HTMLCanvasElement>();
  private readonly urls = new Map<number, string>();
  private readonly textures = new Map<number, THREE.Texture>();
  /** Live authored icons (content editor). Consulted before the item def, so an
   *  edit shows in slots immediately instead of waiting for a reload. */
  private readonly overrides = new Map<number, IconData>();
  /** Bumped whenever any icon is re-rendered. Consumers that cache a painted
   *  icon (the hotbar's per-slot key, pooled drop sprites) compare this so an
   *  authored texture actually reaches the screen — an edit changes neither the
   *  item id nor its count, so those caches would otherwise never notice. */
  private rev = 0;

  get revision(): number { return this.rev; }

  constructor(
    private readonly atlas: HTMLCanvasElement,
    private readonly reg: Registries,
    private readonly table: RenderTable,
  ) {}

  /** Data URL for the item's icon, or '' for the empty item. */
  get(itemId: number): string {
    if (itemId === 0) return '';
    let url = this.urls.get(itemId);
    if (url === undefined) { url = this.canvasFor(itemId).toDataURL(); this.urls.set(itemId, url); }
    return url;
  }

  /** A cached nearest-filtered THREE texture of the icon (dropped-item sprites). */
  texture(itemId: number): THREE.Texture {
    let tex = this.textures.get(itemId);
    if (!tex) {
      tex = new THREE.CanvasTexture(this.canvasFor(itemId));
      tex.magFilter = THREE.NearestFilter;
      tex.minFilter = THREE.NearestFilter;
      tex.colorSpace = THREE.SRGBColorSpace;
      this.textures.set(itemId, tex);
    }
    return tex;
  }

  /** Install (or clear, with null) an authored icon for an item and drop its
   *  cached render so the next read repaints. */
  setOverride(itemId: number, icon: IconData | null): void {
    if (icon) this.overrides.set(itemId, icon);
    else this.overrides.delete(itemId);
    this.invalidate(itemId);
  }

  /** The rendered icon canvas, for reading pixels back (the content editor seeds
   *  its texture editor from whatever the item looks like today). Synchronous —
   *  decoding the data URL through an Image would not be. */
  canvas(itemId: number): HTMLCanvasElement {
    return this.canvasFor(itemId);
  }

  /** Drop the cached icon for an item so it re-renders from the (re-skinned)
   *  atlas on next access — used when the Texture Forge re-skins a block (M34). */
  invalidate(itemId: number): void {
    this.canvases.delete(itemId);
    this.urls.delete(itemId);
    this.textures.get(itemId)?.dispose();
    this.textures.delete(itemId);
    this.rev++;
  }

  private canvasFor(itemId: number): HTMLCanvasElement {
    const hit = this.canvases.get(itemId);
    if (hit) return hit;
    const cvs = document.createElement('canvas');
    cvs.width = TILE_PX;
    cvs.height = TILE_PX;
    const ctx = cvs.getContext('2d')!;
    const name = this.reg.items.nameOf[itemId] ?? '';
    const def = this.reg.items.byName.get(name);

    let painted = false;
    // A data-driven icon (content editor) wins over both the block-face sample
    // and the procedural sprite — it is the author saying what this looks like.
    const authored = this.overrides.get(itemId) ?? def?.icon;
    if (authored) { paintIconData(ctx, authored); painted = true; }
    if (!painted && def?.block) {
      const state = this.reg.states.idByKey.get(`${def.block}[axis=y]`) ?? this.reg.states.defaultOf.get(def.block);
      if (state !== undefined) {
        let tile = this.table.tiles[state * 6 + 5]!; // +z side
        if (tile < 0) tile = this.table.tiles[state * 6 + 3]!; // fall back to +y top
        if (tile >= 0) { ctx.drawImage(this.atlas, tile * TILE_PX, 0, TILE_PX, TILE_PX, 0, 0, TILE_PX, TILE_PX); painted = true; }
      }
    }
    if (!painted) paintLooseItem(ctx, name, def?.toolClass, def?.toolTier);
    this.canvases.set(itemId, cvs);
    return cvs;
  }
}

/** Blit an authored icon (palette + per-pixel indices) onto the tile. */
function paintIconData(ctx: CanvasRenderingContext2D, icon: IconData): void {
  const rgba = iconToRgba(icon);
  const img = ctx.createImageData(ICON_SIZE, ICON_SIZE);
  img.data.set(rgba);
  ctx.putImageData(img, 0, 0);
}

function set(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, g: number, b: number): void {
  ctx.fillStyle = `rgb(${r | 0},${g | 0},${b | 0})`;
  ctx.fillRect(x, y, 1, 1);
}

function line(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, c: [number, number, number], w = 1): void {
  const dx = x1 - x0, dy = y1 - y0;
  const steps = Math.max(Math.abs(dx), Math.abs(dy));
  for (let i = 0; i <= steps; i++) {
    const x = Math.round(x0 + (dx * i) / steps), y = Math.round(y0 + (dy * i) / steps);
    for (let ox = 0; ox < w; ox++) for (let oy = 0; oy < w; oy++) set(ctx, x + ox, y + oy, c[0], c[1], c[2]);
  }
}

/** Procedural sprite for non-block items. */
function paintLooseItem(ctx: CanvasRenderingContext2D, name: string, toolClass?: string, toolTier?: string): void {
  const wood: [number, number, number] = [120, 86, 46];
  if (name === 'stick') {
    line(ctx, 3, 12, 12, 3, wood, 2);
    return;
  }
  if (name === 'coal') {
    for (let y = 3; y < 13; y++) for (let x = 3; x < 13; x++) {
      const d = Math.abs(x - 8) + Math.abs(y - 8);
      if (d > 6) continue;
      const v = 0.7 + ((x * 7 + y * 13) % 5) * 0.08;
      set(ctx, x, y, 40 * v, 40 * v, 44 * v);
    }
    return;
  }
  if (name === 'snowball') {
    for (let y = 3; y < 13; y++) for (let x = 3; x < 13; x++) {
      if (Math.hypot(x - 8, y - 8) > 5) continue;
      const v = 0.9 + ((x * 5 + y * 7) % 3) * 0.05;
      set(ctx, x, y, 235 * v, 242 * v, 250 * v);
    }
    return;
  }
  if (name === 'shears') {
    line(ctx, 4, 12, 12, 4, [206, 206, 212], 2); // two crossed blades
    line(ctx, 12, 12, 4, 4, [168, 168, 174], 2);
    set(ctx, 8, 8, 90, 90, 96); // pivot
    return;
  }
  // ── Combat gear (M17) — small distinct sprites ──
  const armorTint: [number, number, number] =
    name.startsWith('iron_') ? [216, 216, 222]
      : name.startsWith('diamond_') ? [104, 220, 216]
        : name.startsWith('netherite_') ? [78, 70, 76]
          : name.startsWith('golden_') ? [244, 214, 84]
            : [176, 130, 92]; // leather / turtle-ish default
  if (name.endsWith('_helmet') || name === 'turtle_helmet') {
    const t: [number, number, number] = name === 'turtle_helmet' ? [86, 150, 88] : armorTint;
    for (let x = 4; x <= 11; x++) for (let y = 4; y <= 8; y++) if (Math.hypot(x - 7.5, y - 8) < 4.2) set(ctx, x, y, t[0], t[1], t[2]);
    for (let x = 4; x <= 11; x++) set(ctx, x, 9, t[0] * 0.8, t[1] * 0.8, t[2] * 0.8); // brim
    return;
  }
  if (name.endsWith('_chestplate')) {
    for (let x = 4; x <= 11; x++) for (let y = 4; y <= 12; y++) set(ctx, x, y, armorTint[0], armorTint[1], armorTint[2]);
    for (let y = 4; y <= 7; y++) { set(ctx, 7, y, 60, 60, 66); set(ctx, 8, y, 60, 60, 66); } // neckline
    return;
  }
  if (name.endsWith('_leggings')) {
    for (let x = 4; x <= 11; x++) for (let y = 3; y <= 7; y++) set(ctx, x, y, armorTint[0], armorTint[1], armorTint[2]);
    for (let y = 8; y <= 13; y++) { for (let x = 4; x <= 6; x++) set(ctx, x, y, armorTint[0], armorTint[1], armorTint[2]); for (let x = 9; x <= 11; x++) set(ctx, x, y, armorTint[0], armorTint[1], armorTint[2]); }
    return;
  }
  if (name.endsWith('_boots')) {
    for (let x = 4; x <= 6; x++) for (let y = 3; y <= 11; y++) set(ctx, x, y, armorTint[0], armorTint[1], armorTint[2]);
    for (let x = 9; x <= 11; x++) for (let y = 3; y <= 11; y++) set(ctx, x, y, armorTint[0], armorTint[1], armorTint[2]);
    for (let x = 3; x <= 12; x++) set(ctx, x, 12, armorTint[0] * 0.8, armorTint[1] * 0.8, armorTint[2] * 0.8); // soles
    return;
  }
  if (name === 'shield') {
    for (let x = 4; x <= 11; x++) for (let y = 2; y <= 13; y++) {
      const w = y < 10 ? 0 : (y - 9) * 1.4; // taper to a point at the bottom
      if (x >= 4 + w && x <= 11 - w) set(ctx, x, y, 120, 88, 52);
    }
    line(ctx, 7, 3, 7, 12, [206, 206, 212], 1); // metal boss cross
    line(ctx, 4, 6, 11, 6, [206, 206, 212], 1);
    return;
  }
  if (name === 'totem_of_undying') {
    for (let x = 5; x <= 10; x++) for (let y = 3; y <= 12; y++) set(ctx, x, y, 244, 214, 84); // gold idol body
    set(ctx, 6, 5, 60, 140, 90); set(ctx, 9, 5, 60, 140, 90); // emerald eyes
    for (let x = 3; x <= 12; x++) set(ctx, x, 8, 220, 190, 70); // outstretched arms
    return;
  }
  if (name === 'mace') {
    line(ctx, 5, 13, 10, 8, wood, 2); // handle
    for (let x = 8; x <= 13; x++) for (let y = 2; y <= 7; y++) if (Math.abs(x - 10.5) + Math.abs(y - 4.5) < 4) set(ctx, x, y, 70, 66, 72); // heavy head
    set(ctx, 10, 4, 140, 136, 142); // highlight
    return;
  }
  // ── Cartography (M18) ──
  if (name === 'paper') {
    for (let x = 4; x <= 11; x++) for (let y = 3; y <= 12; y++) set(ctx, x, y, 236, 236, 226);
    for (const y of [5, 7, 9]) for (let x = 5; x <= 10; x++) set(ctx, x, y, 176, 176, 168); // ruled lines
    return;
  }
  if (name === 'compass') {
    for (let x = 3; x <= 12; x++) for (let y = 3; y <= 12; y++) if (Math.hypot(x - 7.5, y - 7.5) < 4.6) set(ctx, x, y, 150, 154, 162); // rim
    for (let x = 3; x <= 12; x++) for (let y = 3; y <= 12; y++) if (Math.hypot(x - 7.5, y - 7.5) < 3.3) set(ctx, x, y, 40, 44, 54); // face
    line(ctx, 7, 10, 8, 5, [230, 66, 66], 1); // red north needle
    line(ctx, 8, 5, 8, 10, [220, 220, 228], 1); // white south needle
    return;
  }
  if (name === 'map' || name === 'filled_map') {
    for (let x = 3; x <= 12; x++) for (let y = 3; y <= 12; y++) set(ctx, x, y, 226, 210, 170); // parchment
    for (let x = 3; x <= 12; x++) { set(ctx, x, 3, 168, 120, 74); set(ctx, x, 12, 168, 120, 74); } // border
    for (let y = 3; y <= 12; y++) { set(ctx, 3, y, 168, 120, 74); set(ctx, 12, y, 168, 120, 74); }
    if (name === 'filled_map') {
      for (let x = 5; x <= 10; x++) for (let y = 5; y <= 10; y++) set(ctx, x, y, 96, 150, 88); // sampled land
      set(ctx, 8, 8, 220, 60, 60); set(ctx, 7, 8, 220, 60, 60); // player marker
    }
    return;
  }
  // ── Amethyst (M19) ──
  if (name === 'amethyst_shard') {
    for (let y = 3; y <= 13; y++) {
      const half = y < 8 ? y - 3 : 12 - y; // diamond/shard silhouette
      for (let x = 8 - half; x <= 8 + half; x++) {
        const edge = x === 8 - half || x === 8 + half;
        set(ctx, x, y, edge ? 120 : 160, edge ? 74 : 104, edge ? 190 : 224);
      }
    }
    line(ctx, 8, 4, 8, 12, [206, 176, 244], 1); // bright central facet
    return;
  }
  // ── Ocean monument (M18) — guardian drops ──
  if (name === 'prismarine_shard') {
    // A jagged teal shard with a lighter core.
    for (let y = 3; y <= 13; y++) {
      const half = y < 8 ? y - 3 : 12 - y;
      for (let x = 8 - half; x <= 8 + half; x++) {
        const edge = x === 8 - half || x === 8 + half;
        set(ctx, x, y, edge ? 58 : 96, edge ? 128 : 176, edge ? 118 : 158);
      }
    }
    line(ctx, 8, 4, 8, 12, [180, 224, 206], 1); // bright seam
    return;
  }
  if (name === 'prismarine_crystal') {
    // A glowing pale-green crystal spar (sea-lantern light).
    for (let y = 3; y <= 13; y++) {
      const half = y < 8 ? Math.max(0, y - 5) : Math.max(0, 11 - y);
      for (let x = 8 - half; x <= 8 + half; x++) set(ctx, x, y, 176, 232, 176);
    }
    for (let y = 3; y <= 13; y++) set(ctx, 8, y, 236, 252, 224); // luminous core
    return;
  }
  // ── Pitcher plant (M45) — the sniffer's other grown plant + its pod ──
  if (name === 'pitcher_pod') {
    for (let y = 5; y <= 12; y++) { const half = y <= 8 ? y - 4 : 12 - y; for (let x = 8 - half; x <= 7 + half; x++) set(ctx, x, y, 84, 132, 96); } // green pod
    set(ctx, 7, 4, 120, 90, 140); set(ctx, 8, 4, 120, 90, 140); // magenta tip
    return;
  }
  if (name === 'pitcher_plant') {
    for (let y = 8; y <= 14; y++) { set(ctx, 7, y, 62, 118, 96); set(ctx, 8, y, 80, 138, 112); } // stalk
    for (let y = 4; y <= 9; y++) for (let x = 5; x <= 10; x++) if (Math.abs(x - 7.5) < (y - 3) * 0.8) set(ctx, x, y, 96, 156, 152); // cup
    for (let x = 6; x <= 9; x++) set(ctx, x, 4, 154, 92, 144); // lip
    return;
  }
  // ── Torchflower (M44) — the sniffer-grown flower ──
  if (name === 'torchflower') {
    for (let y = 8; y <= 14; y++) { set(ctx, 7, y, 78, 138, 70); set(ctx, 8, y, 96, 158, 84); } // stem
    for (let y = 3; y <= 7; y++) for (let x = 5; x <= 10; x++) if (Math.abs(x - 7.5) + Math.abs(y - 5) < 3.5) set(ctx, x, y, 234, 142, 46); // orange petals
    for (const [x, y] of [[7, 5], [8, 5], [7, 4], [8, 6]] as const) set(ctx, x, y, 64, 42, 30); // torch-like centre
    return;
  }
  // ── Sniffer (M19) — sniffer egg + torchflower seeds ──
  if (name === 'sniffer_egg') {
    for (let y = 3; y <= 13; y++) { const half = y < 8 ? Math.min(4, y - 2) : Math.min(4, 13 - y); for (let x = 8 - half; x <= 7 + half; x++) set(ctx, x, y, 92, 150, 120); } // mossy egg
    for (const [x, y] of [[7, 6], [9, 8], [6, 9], [8, 11]] as const) set(ctx, x, y, 60, 110, 84); // speckles
    return;
  }
  if (name === 'torchflower_seeds') {
    for (const [x, y] of [[6, 6], [9, 6], [7, 9], [10, 9], [6, 11], [9, 12]] as const) { set(ctx, x, y, 214, 150, 60); set(ctx, x + 1, y, 180, 116, 40); } // scattered orange seeds
    return;
  }
  // ── Cotton — a white boll on a short stem, and its dark seeds ──
  if (name === 'cotton') {
    for (let y = 4; y <= 10; y++) for (let x = 4; x <= 11; x++) {
      if (Math.hypot(x - 7.5, y - 7) > 3.8) continue;
      const v = 0.92 + ((x * 5 + y * 3) % 3) * 0.04; // faint fluff variation
      set(ctx, x, y, 244 * v, 242 * v, 234 * v);
    }
    for (const [x, y] of [[6, 6], [9, 7], [7, 9]] as const) set(ctx, x, y, 208, 204, 192); // shadowed tufts
    for (let y = 10; y <= 13; y++) set(ctx, 7, y, 96, 132, 62); // stem
    return;
  }
  if (name === 'cotton_seeds') {
    for (const [x, y] of [[6, 6], [9, 6], [7, 9], [10, 9], [6, 11], [9, 12]] as const) { set(ctx, x, y, 92, 76, 54); set(ctx, x + 1, y, 64, 52, 36); } // scattered dark seeds
    return;
  }
  // ── Fish buckets (M18) — a water pail with a fish in it ──
  if (name.endsWith('_bucket') && (name.startsWith('cod') || name.startsWith('salmon') || name.startsWith('tropical_fish') || name.startsWith('pufferfish'))) {
    for (let y = 6; y <= 13; y++) { const inset = y >= 12 ? 1 : 0; for (let x = 4 + inset; x <= 11 - inset; x++) set(ctx, x, y, 150, 158, 168); } // steel pail
    for (let x = 4; x <= 11; x++) set(ctx, x, 6, 120, 128, 140); // rim
    for (let x = 5; x <= 10; x++) for (let y = 7; y <= 10; y++) set(ctx, x, y, 60, 120, 200); // water
    const fishCol: [number, number, number] = name.startsWith('salmon') ? [200, 96, 84] : name.startsWith('tropical_fish') ? [224, 138, 42] : name.startsWith('pufferfish') ? [220, 200, 90] : [183, 154, 111];
    for (const [x, y] of [[7, 8], [8, 8], [9, 8], [6, 8]] as const) set(ctx, x, y, fishCol[0], fishCol[1], fishCol[2]); // fish body
    return;
  }
  // ── Glow squid (M22) — glow ink sac ──
  if (name === 'glow_ink_sac' || name === 'ink_sac') {
    const glow = name === 'glow_ink_sac';
    for (let y = 5; y <= 12; y++) { // a rounded sac
      const half = y <= 8 ? y - 3 : 13 - y;
      for (let x = 8 - half; x <= 7 + half; x++) set(ctx, x, y, glow ? 46 : 26, glow ? 190 : 26, glow ? 200 : 34);
    }
    if (glow) for (const [x, y] of [[8, 7], [6, 9], [9, 10]] as const) set(ctx, x, y, 170, 244, 250); // luminous flecks
    return;
  }
  // ── Archaeology (M41) — brush + pottery sherds ──
  if (name === 'brush') {
    line(ctx, 4, 13, 9, 6, [150, 110, 60], 2); // wooden handle, lower-left → up
    for (let x = 9; x <= 12; x++) set(ctx, x, 4, 214, 122, 88); // copper ferrule
    for (let x = 9; x <= 13; x++) for (let y = 2; y <= 4; y++) set(ctx, x, y, 236, 236, 224); // pale bristles
    return;
  }
  if (name.endsWith('_pottery_sherd')) {
    // A terracotta shard: an irregular slab with a scratched motif.
    for (let y = 4; y <= 12; y++) {
      const half = y <= 8 ? y - 3 : 13 - y;
      for (let x = 8 - half; x <= 7 + half; x++) {
        const edge = x === 8 - half || x === 7 + half;
        set(ctx, x, y, edge ? 150 : 190, edge ? 92 : 122, edge ? 70 : 96);
      }
    }
    for (const [x, y] of [[8, 6], [7, 8], [9, 9], [8, 10]] as const) set(ctx, x, y, 120, 70, 52); // incised motif
    return;
  }
  // ── Trial chambers (M22) — wind charge (breeze projectile) ──
  if (name === 'wind_charge') {
    for (let y = 4; y <= 12; y++) { // a pale cyan swirling orb
      const half = y <= 8 ? y - 3 : 13 - y;
      for (let x = 8 - half; x <= 7 + half; x++) {
        const edge = x === 8 - half || x === 7 + half;
        set(ctx, x, y, edge ? 120 : 176, edge ? 190 : 226, edge ? 200 : 236);
      }
    }
    for (let t = 0; t < 6; t++) set(ctx, 8 + t - 3, 8 - ((t - 3) >> 1), 92, 150, 168); // inner swirl
    return;
  }
  // ── Turtle (M18) — scute (baby-turtle scale, → turtle helmet) ──
  if (name === 'scute') {
    for (let y = 4; y <= 12; y++) {
      const half = y <= 8 ? y - 3 : 13 - y; // rounded shell-scale silhouette
      for (let x = 8 - half; x <= 7 + half; x++) {
        const edge = x === 8 - half || x === 7 + half;
        set(ctx, x, y, edge ? 78 : 128, edge ? 128 : 178, edge ? 62 : 96);
      }
    }
    for (const [x, y] of [[8, 7], [6, 9], [10, 9], [8, 11]] as const) set(ctx, x, y, 92, 138, 70); // scute segments
    return;
  }
  // ── Nether (M20) — barter currency ──
  if (name === 'gold_ingot') {
    for (let y = 6; y <= 10; y++) {
      const inset = y === 6 || y === 10 ? 1 : 0; // slightly bevelled ingot bar
      for (let x = 3 + inset; x <= 12 - inset; x++) {
        const hi = y <= 7; // top face catches the light
        set(ctx, x, y, hi ? 250 : 220, hi ? 224 : 178, hi ? 96 : 56);
      }
    }
    return;
  }
  if (name === 'nether_star') {
    // A pale four-pointed star with a bright core — the Wither's drop.
    for (let i = -6; i <= 6; i++) {
      const fade = 1 - Math.abs(i) / 7;
      set(ctx, 8 + i, 8, 200 + 40 * fade, 210 + 40 * fade, 200 + 55 * fade); // horizontal spar
      set(ctx, 8, 8 + i, 200 + 40 * fade, 210 + 40 * fade, 200 + 55 * fade); // vertical spar
    }
    for (let i = -3; i <= 3; i++) { // diagonal glints
      set(ctx, 8 + i, 8 + i, 180, 196, 190);
      set(ctx, 8 + i, 8 - i, 180, 196, 190);
    }
    for (const [x, y] of [[8, 8], [7, 8], [9, 8], [8, 7], [8, 9]] as const) set(ctx, x, y, 245, 250, 245); // white-hot core
    return;
  }
  // ── Trial chambers (M22) ──
  if (name === 'trial_key' || name === 'ominous_trial_key') {
    const om = name === 'ominous_trial_key';
    const r = om ? 120 : 210, g = om ? 60 : 190, b = om ? 190 : 70; // ominous = purple, normal = amber
    for (let y = 3; y <= 8; y++) for (let x = 6; x <= 9; x++) { const edge = x === 6 || x === 9 || y === 3; set(ctx, x, y, edge ? r * 0.7 : r, edge ? g * 0.7 : g, edge ? b * 0.7 : b); } // bow
    set(ctx, 8, 6, 30, 30, 34); set(ctx, 8, 7, 30, 30, 34); // keyhole
    for (let y = 8; y <= 13; y++) set(ctx, 8, y, r, g, b); // shaft
    set(ctx, 9, 11, r, g, b); set(ctx, 9, 13, r, g, b); // teeth
    return;
  }
  if (name === 'ominous_bottle') {
    for (let y = 4; y <= 6; y++) for (let x = 7; x <= 8; x++) set(ctx, x, y, 120, 120, 130); // neck
    for (let y = 6; y <= 13; y++) { const half = y < 9 ? y - 5 : 4; for (let x = 8 - half; x <= 7 + half; x++) set(ctx, x, y, 92, 46, 140); } // dark potion body
    for (let x = 6; x <= 9; x++) set(ctx, x, 8, 168, 120, 210); // ominous swirl
    return;
  }
  // ── Nether fauna (M17) — strider steering ──
  if (name === 'warped_fungus_on_a_stick') {
    line(ctx, 4, 13, 10, 5, [120, 90, 40], 2); // wooden stick, lower-left → upper-right
    line(ctx, 10, 5, 12, 3, [90, 130, 130], 1); // string to the dangling fungus
    for (let y = 2; y <= 5; y++) for (let x = 11; x <= 13; x++) set(ctx, x, y, 40, 150, 140); // teal warped-fungus cap
    set(ctx, 12, 6, 210, 220, 200); set(ctx, 12, 7, 210, 220, 200); // pale stem
    return;
  }
  // ── Copper (MB.1) ──
  if (name === 'copper_ingot') {
    for (let y = 6; y <= 10; y++) {
      const inset = y === 6 || y === 10 ? 1 : 0; // bevelled ingot bar
      for (let x = 3 + inset; x <= 12 - inset; x++) {
        const hi = y <= 7; // top face catches the light
        set(ctx, x, y, hi ? 214 : 176, hi ? 122 : 92, hi ? 88 : 62);
      }
    }
    return;
  }
  if (name === 'honeycomb') {
    // A golden hexagon with darker cell seams.
    for (let y = 4; y <= 12; y++) {
      const half = y <= 8 ? y - 3 : 13 - y; // hexagon silhouette
      for (let x = 8 - half; x <= 7 + half; x++) set(ctx, x, y, 232, 168, 44);
    }
    for (const [x, y] of [[8, 6], [6, 8], [10, 8], [8, 10]] as const) set(ctx, x, y, 176, 116, 24); // cell seams
    return;
  }
  // ── The End (M21) ──
  if (name === 'elytra') {
    // A grey membranous wing pair meeting at the spine.
    for (let y = 3; y <= 12; y++) {
      const span = y <= 7 ? 1 + (y - 3) : 12 - y + 2; // widen then taper
      for (let d = 1; d <= span; d++) {
        const shade = d === span ? 130 : 180 - d * 4; // darker trailing edge
        set(ctx, 7 - d, y, shade - 20, shade - 8, shade); // left wing (violet-grey)
        set(ctx, 8 + d, y, shade - 20, shade - 8, shade); // right wing
      }
    }
    for (let y = 3; y <= 12; y++) { set(ctx, 7, y, 90, 84, 110); set(ctx, 8, y, 90, 84, 110); } // spine
    return;
  }
  if (name === 'firework_rocket') {
    for (let y = 4; y <= 11; y++) for (let x = 6; x <= 9; x++) set(ctx, x, y, 210, 210, 216); // paper tube
    for (let x = 6; x <= 9; x++) { set(ctx, x, 4, 214, 66, 66); set(ctx, x, 5, 214, 66, 66); } // red cap
    line(ctx, 7, 12, 7, 14, [120, 90, 40], 1); // fuse
    set(ctx, 7, 14, 250, 210, 90); // spark
    return;
  }
  // ── Deep Dark (M22) ──
  if (name === 'sculk_catalyst') {
    // A dark sculk cube glowing with cyan bloom-flecks.
    for (let y = 3; y <= 12; y++) for (let x = 3; x <= 12; x++) {
      const edge = x === 3 || x === 12 || y === 3 || y === 12;
      set(ctx, x, y, edge ? 8 : 16, edge ? 18 : 32, edge ? 22 : 38);
    }
    for (const [x, y] of [[5, 5], [9, 4], [11, 7], [6, 9], [10, 10], [4, 8], [8, 7]] as const)
      set(ctx, x, y, 74, 224, 224); // glowing flecks
    return;
  }
  if (toolClass && toolTier) {
    paintTool(ctx, toolClass, toolTier, wood);
    return;
  }
  // Generic fallback: a soft grey lozenge.
  for (let y = 5; y < 11; y++) for (let x = 4; x < 12; x++) set(ctx, x, y, 150, 150, 156);
}

function paintTool(ctx: CanvasRenderingContext2D, cls: string, tier: string, wood: [number, number, number]): void {
  const head = TIER_COLOR[tier] ?? [160, 160, 160];
  // Handle: diagonal stick from lower-left toward upper-right.
  line(ctx, 4, 13, 11, 6, wood, 2);
  const H = head;
  const put = (x: number, y: number): void => set(ctx, x, y, H[0], H[1], H[2]);
  switch (cls) {
    case 'pickaxe':
      for (let x = 5; x <= 13; x++) put(x, 3);
      put(5, 4); put(13, 4);
      break;
    case 'axe':
      for (let y = 2; y <= 7; y++) for (let x = 10; x <= 13; x++) if (Math.abs(y - 4) + (13 - x) < 5) put(x, y);
      break;
    case 'shovel':
      for (let y = 2; y <= 6; y++) for (let x = 9; x <= 13; x++) put(x, y);
      break;
    case 'sword':
      line(ctx, 5, 12, 12, 3, H, 2); // bright blade over the handle
      line(ctx, 4, 11, 7, 14, [120, 100, 40], 1); // crossguard
      break;
    case 'hoe':
      for (let x = 10; x <= 14; x++) put(x, 3);
      put(10, 4); put(10, 5);
      break;
    default:
      for (let y = 3; y <= 6; y++) for (let x = 10; x <= 13; x++) put(x, y);
  }
}
